import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";

import { MESSAGES } from "../constants/messages.constant";

export type PreviewRotationDegrees = 0 | 90 | 180 | 270;

export type PreviewZoomMode =
  | "automatic"
  | "actualSize"
  | "pageFit"
  | "pageWidth"
  | "custom";

export interface PreviewToolbarDrawPoint {
  x: number;
  y: number;
}

export interface PreviewToolbarTextAnnotation {
  id: string;
  kind?: "text";
  selectedText: string;
  quotePrefix: string;
  quoteSuffix: string;
  occurrenceIndex: number;
  startOffset: number;
}

export interface PreviewToolbarDrawAnnotation {
  id: string;
  kind: "draw";
  points: PreviewToolbarDrawPoint[];
  strokeWidth: number;
}

export type PreviewToolbarAnnotation =
  | PreviewToolbarTextAnnotation
  | PreviewToolbarDrawAnnotation;

export interface PreviewToolbarState {
  zoomLevel: number;
  zoomMode: PreviewZoomMode;
  fitScale: number;
  rotationDegrees: PreviewRotationDegrees;
  highlightModeEnabled: boolean;
  annotations: PreviewToolbarAnnotation[];
  rendered: boolean;
}

interface TextSegment {
  node: Text;
  start: number;
  end: number;
}

interface PreviewDimensions {
  width: number;
  height: number;
}

interface PreviewToolbarTextSelection {
  annotation: PreviewToolbarTextAnnotation;
  startOffset: number;
  endOffset: number;
}

@Directive({
  selector: "[appPreviewToolbar]",
  standalone: true,
})
export class PreviewToolbarDirective
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input({ required: true }) appPreviewToolbar!: PreviewToolbarState;
  @Input() enableInteractiveHighlight: boolean = false;

  @Output() annotationChange = new EventEmitter<PreviewToolbarAnnotation[]>();
  @Output() fitScaleChange = new EventEmitter<number>();

  private mutationObserver: MutationObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private applyStateScheduled: boolean = false;
  private highlightListenersBound: boolean = false;
  private drawListenersBound: boolean = false;
  private isApplyingState: boolean = false;
  private isDrawingHighlight: boolean = false;
  private activeDrawAnnotation: PreviewToolbarDrawAnnotation | null = null;
  private annotationSequence: number = 0;

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.startObserving();
    this.startResizeObserver();
    this.scheduleApplyState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["appPreviewToolbar"] ||
      changes["enableInteractiveHighlight"]
    ) {
      this.scheduleApplyState();
    }
  }

  ngOnDestroy(): void {
    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.toggleSelectionListeners(false);
    this.toggleDrawListeners(false);
  }

  private startObserving(): void {
    this.mutationObserver = new MutationObserver(() => {
      if (!this.isApplyingState && !this.isDrawingHighlight) {
        this.scheduleApplyState();
      }
    });

    this.observeHostMutations();
  }

  private observeHostMutations(): void {
    this.mutationObserver?.observe(this.hostElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private runWithoutMutationObserver<T>(callback: () => T): T {
    this.mutationObserver?.disconnect();

    try {
      return callback();
    } finally {
      this.observeHostMutations();
    }
  }

  private startResizeObserver(): void {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleApplyState();
    });

    this.resizeObserver.observe(this.hostElement);
  }

  private scheduleApplyState(): void {
    if (this.applyStateScheduled) {
      return;
    }

    this.applyStateScheduled = true;
    requestAnimationFrame(() => {
      this.applyStateScheduled = false;
      this.applyState();
    });
  }

  private applyState(): void {
    const wrapper = this.ensurePreviewWrapper();
    if (!wrapper || !this.appPreviewToolbar) {
      this.toggleSelectionListeners(false);
      this.toggleDrawListeners(false);
      return;
    }

    const hasSelectableText = this.hasSelectableText(wrapper);

    this.isApplyingState = true;
    try {
      this.runWithoutMutationObserver(() => {
        this.clearExistingHighlights(wrapper);
        this.applyWrapperStyles(wrapper);
        this.reapplyAnnotations(wrapper);
      });
    } finally {
      this.isApplyingState = false;
      const shouldEnableHighlight =
        this.enableInteractiveHighlight && this.appPreviewToolbar.rendered;
      let highlightCursor = "";
      if (shouldEnableHighlight) {
        highlightCursor = hasSelectableText ? "copy" : "crosshair";
      }

      this.hostElement.style.cursor = highlightCursor;
      this.hostElement.style.userSelect =
        shouldEnableHighlight && !hasSelectableText ? "none" : "";
      this.toggleSelectionListeners(shouldEnableHighlight);
      this.toggleDrawListeners(shouldEnableHighlight);
    }
  }

  private get hostElement(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  private ensurePreviewWrapper(): HTMLElement | null {
    const existingWrapper = this.hostElement.firstElementChild;
    if (existingWrapper?.classList.contains("docx-preview-wrapper")) {
      return existingWrapper as HTMLElement;
    }

    if (this.hostElement.childNodes.length === 0) {
      return null;
    }

    const wrapper = document.createElement("div");
    wrapper.className = "docx-preview-wrapper";

    while (this.hostElement.firstChild) {
      wrapper.appendChild(this.hostElement.firstChild);
    }

    this.hostElement.appendChild(wrapper);
    return wrapper;
  }

  private applyWrapperStyles(wrapper: HTMLElement): void {
    const fitScale = this.getFitScale(wrapper);
    const previewDimensions = this.getNaturalDocumentDimensions(wrapper);
    const zoomLevel = this.getEffectiveZoomLevel(fitScale);

    if (this.shouldEmitFitScale(fitScale)) {
      this.fitScaleChange.emit(fitScale);
    }

    this.hostElement.style.overflowX = "auto";
    this.hostElement.style.overflowY = "auto";

    wrapper.style.display = "inline-block";
    wrapper.style.minWidth = "100%";
    wrapper.style.width = `${previewDimensions.width}px`;
    wrapper.style.height = `${previewDimensions.height}px`;
    wrapper.style.position = "relative";
    wrapper.style.transformOrigin = this.getTransformOrigin();
    wrapper.style.transform = this.getTransformValue(
      zoomLevel,
      previewDimensions,
    );
  }

  private shouldEmitFitScale(fitScale: number): boolean {
    return Math.abs((this.appPreviewToolbar.fitScale ?? 1) - fitScale) > 0.0001;
  }

  private getTransformOrigin(): string {
    if (this.appPreviewToolbar.rotationDegrees !== 0) {
      return "top left";
    }

    if (
      this.appPreviewToolbar.zoomMode !== "custom" &&
      this.appPreviewToolbar.zoomMode !== "actualSize"
    ) {
      return "top left";
    }

    return "top center";
  }

  private getTransformValue(
    zoomLevel: number,
    previewDimensions: PreviewDimensions,
  ): string {
    const rotationDegrees = this.appPreviewToolbar.rotationDegrees;
    const scaleAndRotateTransform = `scale(${zoomLevel}) rotate(${rotationDegrees}deg)`;
    const rotationOffset = this.getRotationOffset(
      rotationDegrees,
      zoomLevel,
      previewDimensions,
    );

    if (!rotationOffset) {
      return scaleAndRotateTransform;
    }

    return `translate(${rotationOffset.x}px, ${rotationOffset.y}px) ${scaleAndRotateTransform}`;
  }

  private getRotationOffset(
    rotationDegrees: PreviewRotationDegrees,
    zoomLevel: number,
    previewDimensions: PreviewDimensions,
  ): { x: number; y: number } | null {
    if (previewDimensions.width <= 0 || previewDimensions.height <= 0) {
      return null;
    }

    const scaledWidth = Number((previewDimensions.width * zoomLevel).toFixed(4));
    const scaledHeight = Number((previewDimensions.height * zoomLevel).toFixed(4));

    switch (rotationDegrees) {
      case 90:
        return {
          x: scaledHeight,
          y: 0,
        };
      case 180:
        return {
          x: scaledWidth,
          y: scaledHeight,
        };
      case 270:
        return {
          x: 0,
          y: scaledWidth,
        };
      case 0:
      default:
        return null;
    }
  }

  private getEffectiveZoomLevel(fitScale: number): number {
    switch (this.appPreviewToolbar.zoomMode) {
      case "actualSize":
        return 1;
      case "custom":
        return this.appPreviewToolbar.zoomLevel;
      case "automatic":
      case "pageFit":
      case "pageWidth":
      default:
        return fitScale;
    }
  }

  private getFitScale(wrapper: HTMLElement): number {
    const availableWidth = this.getAvailableHostWidth();
    const naturalWidth = this.getNaturalDocumentDimensions(wrapper).width;

    if (availableWidth <= 0 || naturalWidth <= 0) {
      return 1;
    }

    return Number((availableWidth / naturalWidth).toFixed(4));
  }

  private getAvailableHostWidth(): number {
    const computedStyles = globalThis.getComputedStyle(this.hostElement);
    const paddingLeft = Number.parseFloat(computedStyles.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computedStyles.paddingRight) || 0;
    return this.hostElement.clientWidth - paddingLeft - paddingRight;
  }

  private getNaturalDocumentDimensions(wrapper: HTMLElement): PreviewDimensions {
    const docxWrapper = wrapper.querySelector<HTMLElement>(".docx-wrapper");
    const docxDocument = wrapper.querySelector<HTMLElement>(".docx");
    const measuredElement = docxWrapper ?? docxDocument ?? wrapper.firstElementChild;

    if (!measuredElement || !(measuredElement instanceof HTMLElement)) {
      return {
        width: wrapper.scrollWidth,
        height: wrapper.scrollHeight || wrapper.offsetHeight,
      };
    }

    return {
      width:
        measuredElement.scrollWidth ||
        measuredElement.offsetWidth ||
        wrapper.scrollWidth,
      height:
        measuredElement.scrollHeight ||
        measuredElement.offsetHeight ||
        wrapper.scrollHeight ||
        wrapper.offsetHeight,
    };
  }

  private clearExistingHighlights(wrapper: HTMLElement): void {
    const highlightElements = wrapper.querySelectorAll<HTMLElement>(
      "mark[data-preview-annotation-id]",
    );

    highlightElements.forEach((element) => this.unwrapElement(element));

    wrapper
      .querySelectorAll<SVGElement>(".docx-preview-draw-layer")
      .forEach((element) => element.remove());
  }

  private unwrapElement(element: HTMLElement): void {
    const parentNode = element.parentNode;
    if (!parentNode) {
      return;
    }

    while (element.firstChild) {
      parentNode.insertBefore(element.firstChild, element);
    }

    element.remove();
  }

  private reapplyAnnotations(wrapper: HTMLElement): void {
    this.appPreviewToolbar.annotations.forEach((annotation) => {
      if (this.isDrawAnnotation(annotation)) {
        this.renderDrawAnnotation(wrapper, annotation);
        return;
      }

      this.reapplyTextAnnotation(wrapper, annotation);
    });
  }

  private reapplyTextAnnotation(
    wrapper: HTMLElement,
    annotation: PreviewToolbarTextAnnotation,
  ): void {
    const textContent = wrapper.textContent ?? "";
    const startOffset = this.resolveAnnotationOffset(textContent, annotation);
    if (startOffset === null) {
      return;
    }

    const endOffset = startOffset + annotation.selectedText.length;
    if (
      !this.highlightOffsets(wrapper, startOffset, endOffset, annotation.id)
    ) {
      return;
    }
  }

  private resolveAnnotationOffset(
    textContent: string,
    annotation: PreviewToolbarTextAnnotation,
  ): number | null {
    const positions = this.findOccurrences(textContent, annotation.selectedText);
    if (positions.length === 0) {
      return null;
    }

    const contextualMatches = positions.filter((position) =>
      this.matchesContext(textContent, position, annotation),
    );

    if (contextualMatches.includes(annotation.startOffset)) {
      return annotation.startOffset;
    }

    if (contextualMatches.length > annotation.occurrenceIndex) {
      return contextualMatches[annotation.occurrenceIndex] ?? null;
    }

    if (contextualMatches.length > 0) {
      return contextualMatches[0] ?? null;
    }

    if (positions.includes(annotation.startOffset)) {
      return annotation.startOffset;
    }

    return positions[annotation.occurrenceIndex] ?? positions[0] ?? null;
  }

  private matchesContext(
    textContent: string,
    position: number,
    annotation: PreviewToolbarTextAnnotation,
  ): boolean {
    const prefixStart = Math.max(0, position - annotation.quotePrefix.length);
    const prefix = textContent.slice(prefixStart, position);
    const suffixStart = position + annotation.selectedText.length;
    const suffixEnd = suffixStart + annotation.quoteSuffix.length;
    const suffix = textContent.slice(suffixStart, suffixEnd);

    return (
      prefix === annotation.quotePrefix && suffix === annotation.quoteSuffix
    );
  }

  private findOccurrences(textContent: string, selectedText: string): number[] {
    const positions: number[] = [];
    let searchStart = 0;

    while (searchStart < textContent.length) {
      const foundIndex = textContent.indexOf(selectedText, searchStart);
      if (foundIndex === -1) {
        break;
      }
      positions.push(foundIndex);
      searchStart = foundIndex + selectedText.length;
    }

    return positions;
  }

  private createRangeFromOffsets(
    wrapper: HTMLElement,
    startOffset: number,
    endOffset: number,
  ): Range | null {
    const segments = this.buildTextSegments(wrapper);
    const start = this.resolveBoundary(segments, startOffset);
    const end = this.resolveBoundary(segments, endOffset);
    if (!start || !end) {
      return null;
    }

    const range = document.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);
    return range;
  }

  private buildTextSegments(wrapper: HTMLElement): TextSegment[] {
    const segments: TextSegment[] = [];
    const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
    let currentOffset = 0;
    let currentNode = walker.nextNode();

    while (currentNode) {
      const textNode = currentNode as Text;
      const textLength = textNode.textContent?.length ?? 0;
      segments.push({
        node: textNode,
        start: currentOffset,
        end: currentOffset + textLength,
      });
      currentOffset += textLength;
      currentNode = walker.nextNode();
    }

    return segments;
  }

  private resolveBoundary(
    segments: TextSegment[],
    absoluteOffset: number,
  ): { node: Text; offset: number } | null {
    for (const segment of segments) {
      if (absoluteOffset >= segment.start && absoluteOffset <= segment.end) {
        return {
          node: segment.node,
          offset: absoluteOffset - segment.start,
        };
      }
    }

    const lastSegment = segments.at(-1);
    if (!lastSegment) {
      return null;
    }

    return {
      node: lastSegment.node,
      offset: lastSegment.node.textContent?.length ?? 0,
    };
  }

  private toggleSelectionListeners(shouldBind: boolean): void {
    if (shouldBind && !this.highlightListenersBound) {
      this.hostElement.addEventListener("mouseup", this.handleSelectionChange);
      this.hostElement.addEventListener("keyup", this.handleSelectionChange);
      this.highlightListenersBound = true;
      return;
    }

    if (!shouldBind && this.highlightListenersBound) {
      this.hostElement.removeEventListener(
        "mouseup",
        this.handleSelectionChange,
      );
      this.hostElement.removeEventListener(
        "keyup",
        this.handleSelectionChange,
      );
      this.highlightListenersBound = false;
    }
  }

  private toggleDrawListeners(shouldBind: boolean): void {
    if (shouldBind && !this.drawListenersBound) {
      this.hostElement.addEventListener("mousedown", this.handleDrawStart);
      globalThis.addEventListener("mousemove", this.handleDrawMove);
      globalThis.addEventListener("mouseup", this.handleDrawEnd);
      this.drawListenersBound = true;
      return;
    }

    if (!shouldBind && this.drawListenersBound) {
      this.hostElement.removeEventListener("mousedown", this.handleDrawStart);
      globalThis.removeEventListener("mousemove", this.handleDrawMove);
      globalThis.removeEventListener("mouseup", this.handleDrawEnd);
      this.drawListenersBound = false;
      this.cancelActiveDrawHighlight();
    }
  }

  private readonly handleSelectionChange = (): void => {
    setTimeout(() => this.captureCurrentSelection(), 0);
  };

  private readonly handleDrawStart = (event: MouseEvent): void => {
    if (event.button !== 0 || !this.enableInteractiveHighlight) {
      return;
    }

    const wrapper = this.ensurePreviewWrapper();
    if (!wrapper || !this.shouldStartDrawHighlight(wrapper, event)) {
      return;
    }

    const point = this.getLocalDrawPoint(wrapper, event.clientX, event.clientY);
    if (!point) {
      return;
    }

    event.preventDefault();
    this.isDrawingHighlight = true;
    this.activeDrawAnnotation = {
      id: this.buildAnnotationId(),
      kind: "draw",
      points: [point],
      strokeWidth: this.getDrawStrokeWidth(wrapper),
    };

    this.renderActiveDrawAnnotation(wrapper);
  };

  private readonly handleDrawMove = (event: MouseEvent): void => {
    if (!this.isDrawingHighlight || !this.activeDrawAnnotation) {
      return;
    }

    const wrapper = this.ensurePreviewWrapper();
    if (!wrapper) {
      return;
    }

    const point = this.getLocalDrawPoint(wrapper, event.clientX, event.clientY);
    if (!point) {
      return;
    }

    const lastPoint = this.activeDrawAnnotation.points.at(-1);
    if (
      lastPoint &&
      Math.hypot(point.x - lastPoint.x, point.y - lastPoint.y) < 2
    ) {
      return;
    }

    event.preventDefault();
    this.activeDrawAnnotation = {
      ...this.activeDrawAnnotation,
      points: [...this.activeDrawAnnotation.points, point],
    };

    this.renderActiveDrawAnnotation(wrapper);
  };

  private readonly handleDrawEnd = (): void => {
    if (!this.isDrawingHighlight || !this.activeDrawAnnotation) {
      return;
    }

    const annotation = this.activeDrawAnnotation;
    this.cancelActiveDrawHighlight();

    if (annotation.points.length < 2) {
      return;
    }

    this.annotationChange.emit([
      ...this.appPreviewToolbar.annotations,
      annotation,
    ]);
  };

  private captureCurrentSelection(): void {
    if (!this.enableInteractiveHighlight || !this.appPreviewToolbar.rendered) {
      return;
    }

    const selection = globalThis.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }

    const range = selection.getRangeAt(0);
    if (!this.hostElement.contains(range.commonAncestorContainer)) {
      return;
    }

    if (this.isSelectionInsideHighlight(range)) {
      selection.removeAllRanges();
      return;
    }

    const wrapper = this.ensurePreviewWrapper();
    const textSelection = wrapper
      ? this.buildTextSelection(wrapper, range)
      : null;

    if (
      !textSelection ||
      this.hasDuplicateAnnotation(textSelection.annotation)
    ) {
      selection.removeAllRanges();
      return;
    }

    if (
      this.runWithoutMutationObserver(() =>
        this.highlightOffsets(
          wrapper,
          textSelection.startOffset,
          textSelection.endOffset,
          textSelection.annotation.id,
        ),
      )
    ) {
      this.annotationChange.emit([
        ...this.appPreviewToolbar.annotations,
        textSelection.annotation,
      ]);
    }

    selection.removeAllRanges();
  }

  private isSelectionInsideHighlight(range: Range): boolean {
    const highlightAncestor = this.findHighlightAncestor(
      range.commonAncestorContainer,
    );
    return highlightAncestor !== null;
  }

  private findHighlightAncestor(node: Node): HTMLElement | null {
    let current: Node | null = node;

    while (current && current !== this.hostElement) {
      if (
        current instanceof HTMLElement &&
        current.matches("mark[data-preview-annotation-id]")
      ) {
        return current;
      }
      current = current.parentNode;
    }

    return null;
  }

  private buildTextSelection(
    wrapper: HTMLElement,
    range: Range,
  ): PreviewToolbarTextSelection | null {
    const selectedText = range.toString();
    const trimmedSelectedText = selectedText.trim();
    if (trimmedSelectedText.length === 0) {
      return null;
    }

    const offsets = this.getRangeOffsets(wrapper, range);
    if (!offsets) {
      return null;
    }

    const leadingWhitespaceLength =
      selectedText.length - selectedText.trimStart().length;
    const trailingWhitespaceLength =
      selectedText.length - selectedText.trimEnd().length;
    const normalizedStart = offsets.start + leadingWhitespaceLength;
    const normalizedEnd = offsets.end - trailingWhitespaceLength;
    if (normalizedEnd <= normalizedStart) {
      return null;
    }

    const textContent = wrapper.textContent ?? "";
    const normalizedSelectedText = textContent.slice(
      normalizedStart,
      normalizedEnd,
    );
    const prefixStart = Math.max(0, normalizedStart - 20);
    const suffixEnd = Math.min(textContent.length, normalizedEnd + 20);
    const quotePrefix = textContent.slice(prefixStart, normalizedStart);
    const quoteSuffix = textContent.slice(normalizedEnd, suffixEnd);
    const occurrences = this.findOccurrences(textContent, normalizedSelectedText);
    const occurrenceIndex = occurrences.indexOf(normalizedStart);

    return {
      annotation: {
        id: this.buildAnnotationId(),
        selectedText: normalizedSelectedText,
        quotePrefix,
        quoteSuffix,
        occurrenceIndex: occurrenceIndex === -1 ? 0 : occurrenceIndex,
        startOffset: normalizedStart,
      },
      startOffset: normalizedStart,
      endOffset: normalizedEnd,
    };
  }

  private getRangeOffsets(
    wrapper: HTMLElement,
    range: Range,
  ): { start: number; end: number } | null {
    const leadingRange = document.createRange();
    leadingRange.selectNodeContents(wrapper);

    try {
      leadingRange.setEnd(range.startContainer, range.startOffset);
    } catch {
      return null;
    }

    const start = leadingRange.toString().length;
    const end = start + range.toString().length;
    return { start, end };
  }

  private buildAnnotationId(): string {
    if (typeof globalThis.crypto?.randomUUID === "function") {
      return `preview-annotation-${globalThis.crypto.randomUUID()}`;
    }

    return `preview-annotation-${Date.now().toString(36)}-${(this.annotationSequence++)
      .toString(36)
      .padStart(4, "0")}`;
  }

  private hasDuplicateAnnotation(annotation: PreviewToolbarAnnotation): boolean {
    if (this.isDrawAnnotation(annotation)) {
      return false;
    }

    return this.appPreviewToolbar.annotations.some(
      (existingAnnotation) =>
        !this.isDrawAnnotation(existingAnnotation) &&
        existingAnnotation.startOffset === annotation.startOffset &&
        existingAnnotation.selectedText === annotation.selectedText,
    );
  }

  private highlightOffsets(
    wrapper: HTMLElement,
    startOffset: number,
    endOffset: number,
    annotationId: string,
  ): boolean {
    if (endOffset <= startOffset) {
      return false;
    }

    const segments = this.buildTextSegments(wrapper);
    const overlappingSegments = segments.filter((segment) => {
      return segment.end > startOffset && segment.start < endOffset;
    });

    if (overlappingSegments.length === 0) {
      return false;
    }

    let didHighlight = false;

    for (const segment of overlappingSegments) {
      const segmentStartOffset = Math.max(startOffset, segment.start);
      const segmentEndOffset = Math.min(endOffset, segment.end);
      const startInNode = segmentStartOffset - segment.start;
      const endInNode = segmentEndOffset - segment.start;

      if (endInNode <= startInNode) {
        continue;
      }

      const highlightedNode = this.splitTextNode(
        segment.node,
        startInNode,
        endInNode,
      );
      if (!highlightedNode || highlightedNode.textContent?.length === 0) {
        continue;
      }

      const parentNode = highlightedNode.parentNode;
      if (!parentNode) {
        continue;
      }

      const markElement = this.createHighlightMark(annotationId);
      highlightedNode.before(markElement);
      markElement.appendChild(highlightedNode);
      didHighlight = true;
    }

    return didHighlight;
  }

  private splitTextNode(
    textNode: Text,
    startInNode: number,
    endInNode: number,
  ): Text | null {
    const textLength = textNode.textContent?.length ?? 0;
    if (
      startInNode < 0 ||
      endInNode > textLength ||
      startInNode >= endInNode
    ) {
      return null;
    }

    let targetNode = textNode;
    if (startInNode > 0) {
      targetNode = targetNode.splitText(startInNode);
    }

    const targetLength = endInNode - startInNode;
    if (targetLength < targetNode.length) {
      targetNode.splitText(targetLength);
    }

    return targetNode;
  }

  private createHighlightMark(annotationId: string): HTMLElement {
    const markElement = document.createElement("mark");
    markElement.dataset["previewAnnotationId"] = annotationId;
    markElement.style.backgroundColor = "var(--docx-highlight-background)";
    markElement.style.backgroundImage = "none";
    markElement.style.boxShadow = "none";
    markElement.style.color = "var(--docx-highlight-color, inherit)";
    markElement.style.padding = "0";
    markElement.style.borderRadius = "0";
    markElement.style.position = "relative";
    markElement.style.boxDecorationBreak = "clone";
    markElement.style.setProperty("-webkit-box-decoration-break", "clone");
    markElement.style.textShadow = "none";

    const deleteButton = this.createHighlightDeleteButton(annotationId);
    const showDeleteButton = (): void => {
      deleteButton.style.opacity = "1";
      deleteButton.style.pointerEvents = "auto";
    };
    const hideDeleteButton = (): void => {
      deleteButton.style.opacity = "0";
      deleteButton.style.pointerEvents = "none";
    };

    markElement.addEventListener("mouseenter", showDeleteButton);
    markElement.addEventListener("mouseleave", hideDeleteButton);
    markElement.addEventListener("focusin", showDeleteButton);
    markElement.addEventListener("focusout", hideDeleteButton);

    markElement.appendChild(deleteButton);

    return markElement;
  }

  private createHighlightDeleteButton(annotationId: string): HTMLButtonElement {
    const deleteButton = document.createElement("button");
    deleteButton.type = "button";
    deleteButton.dataset["previewAnnotationDeleteId"] = annotationId;
    deleteButton.setAttribute(
      "aria-label",
      MESSAGES.DOCX_PREVIEW.DELETE_HIGHLIGHT_LABEL,
    );
    deleteButton.title = MESSAGES.DOCX_PREVIEW.DELETE_HIGHLIGHT_LABEL;
    deleteButton.style.position = "absolute";
    deleteButton.style.top = "-0.7rem";
    deleteButton.style.right = "-0.7rem";
    deleteButton.style.display = "inline-flex";
    deleteButton.style.alignItems = "center";
    deleteButton.style.justifyContent = "center";
    deleteButton.style.width = "1.2rem";
    deleteButton.style.height = "1.2rem";
    deleteButton.style.border = "0";
    deleteButton.style.borderRadius = "999px";
    deleteButton.style.padding = "0";
    deleteButton.style.background = "#f2f2f2";
    deleteButton.style.color = "#6b7280";
    deleteButton.style.cursor = "pointer";
    deleteButton.style.opacity = "0";
    deleteButton.style.pointerEvents = "none";
    deleteButton.style.boxShadow = "0 0 0 1px rgba(107, 114, 128, 0.35)";
    deleteButton.style.transition = "opacity 120ms ease-in-out";
    deleteButton.style.zIndex = "1";
    deleteButton.innerHTML = `
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" width="10" height="10">
        <path d="M3.2 3.2 12.8 12.8M12.8 3.2 3.2 12.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.8" />
      </svg>
    `;
    deleteButton.addEventListener("mousedown", (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
    deleteButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.removeAnnotation(annotationId);
    });

    return deleteButton;
  }

  private removeAnnotation(annotationId: string): void {
    const nextAnnotations = this.appPreviewToolbar.annotations.filter(
      (annotation) => annotation.id !== annotationId,
    );

    if (nextAnnotations.length === this.appPreviewToolbar.annotations.length) {
      return;
    }

    this.annotationChange.emit(nextAnnotations);
  }

  private shouldUseDrawHighlight(wrapper: HTMLElement): boolean {
    return !this.hasSelectableText(wrapper);
  }

  private shouldStartDrawHighlight(
    wrapper: HTMLElement,
    event: MouseEvent,
  ): boolean {
    return (
      this.shouldUseDrawHighlight(wrapper) ||
      this.isImageLikeTarget(event.target)
    );
  }

  private hasSelectableText(wrapper: HTMLElement): boolean {
    return this.buildTextSegments(wrapper).some((segment) => {
      return (segment.node.textContent ?? "").trim().length > 0;
    });
  }

  private isImageLikeTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    return target.closest("img, canvas, picture, svg image") !== null;
  }

  private isDrawAnnotation(
    annotation: PreviewToolbarAnnotation,
  ): annotation is PreviewToolbarDrawAnnotation {
    return annotation.kind === "draw";
  }

  private renderDrawAnnotation(
    wrapper: HTMLElement,
    annotation: PreviewToolbarDrawAnnotation,
  ): void {
    const drawLayer = this.ensureDrawLayer(wrapper);
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.dataset["previewAnnotationId"] = annotation.id;
    path.setAttribute("d", this.buildDrawPath(annotation.points));
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "var(--docx-highlight-background)");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    path.setAttribute("stroke-opacity", "0.55");
    path.setAttribute("stroke-width", `${annotation.strokeWidth}`);
    path.style.pointerEvents = "stroke";
    path.addEventListener("mouseenter", () => {
      this.showDrawDeleteButton(drawLayer, annotation);
    });
    path.addEventListener("mouseleave", () => {
      this.hideDrawDeleteButton(drawLayer, annotation.id);
    });
    drawLayer.appendChild(path);

    this.renderDrawDeleteButton(drawLayer, annotation);
  }

  private ensureDrawLayer(wrapper: HTMLElement): SVGSVGElement {
    const previewDimensions = this.getNaturalDocumentDimensions(wrapper);
    const existingLayer = wrapper.querySelector<SVGSVGElement>(
      ".docx-preview-draw-layer",
    );

    if (existingLayer) {
      existingLayer.setAttribute(
        "viewBox",
        `0 0 ${previewDimensions.width} ${previewDimensions.height}`,
      );
      return existingLayer;
    }

    const drawLayer = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    drawLayer.classList.add("docx-preview-draw-layer");
    drawLayer.setAttribute(
      "viewBox",
      `0 0 ${previewDimensions.width} ${previewDimensions.height}`,
    );
    drawLayer.setAttribute("preserveAspectRatio", "none");
    drawLayer.style.position = "absolute";
    drawLayer.style.inset = "0";
    drawLayer.style.width = "100%";
    drawLayer.style.height = "100%";
    drawLayer.style.pointerEvents = "none";
    drawLayer.style.overflow = "visible";
    drawLayer.style.zIndex = "2";
    drawLayer.style.mixBlendMode = "multiply";
    wrapper.appendChild(drawLayer);
    return drawLayer;
  }

  private renderActiveDrawAnnotation(wrapper: HTMLElement): void {
    if (!this.activeDrawAnnotation) {
      return;
    }

    const drawLayer = this.ensureDrawLayer(wrapper);
    const existingPath = drawLayer.querySelector<SVGPathElement>(
      `[data-preview-annotation-id="${this.activeDrawAnnotation.id}"]`,
    );
    const path =
      existingPath ??
      document.createElementNS("http://www.w3.org/2000/svg", "path");

    path.dataset["previewAnnotationId"] = this.activeDrawAnnotation.id;
    path.setAttribute("d", this.buildDrawPath(this.activeDrawAnnotation.points));
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "var(--docx-highlight-background)");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    path.setAttribute("stroke-opacity", "0.55");
    path.setAttribute(
      "stroke-width",
      `${this.activeDrawAnnotation.strokeWidth}`,
    );

    if (!existingPath) {
      drawLayer.appendChild(path);
    }
  }

  private renderDrawDeleteButton(
    drawLayer: SVGSVGElement,
    annotation: PreviewToolbarDrawAnnotation,
  ): void {
    const existingButton = drawLayer.querySelector<SVGForeignObjectElement>(
      `[data-preview-annotation-delete-id="${annotation.id}"]`,
    );

    if (existingButton) {
      this.positionDrawDeleteButton(existingButton, annotation);
      return;
    }

    const deleteButton = this.createDrawDeleteButton(annotation.id);
    this.positionDrawDeleteButton(deleteButton, annotation);
    drawLayer.appendChild(deleteButton);
  }

  private showDrawDeleteButton(
    drawLayer: SVGSVGElement,
    annotation: PreviewToolbarDrawAnnotation,
  ): void {
    const deleteButton = drawLayer.querySelector<SVGForeignObjectElement>(
      `[data-preview-annotation-delete-id="${annotation.id}"]`,
    );

    if (!deleteButton) {
      return;
    }

    deleteButton.style.opacity = "1";
    deleteButton.style.pointerEvents = "auto";
  }

  private hideDrawDeleteButton(
    drawLayer: SVGSVGElement,
    annotationId: string,
  ): void {
    const deleteButton = drawLayer.querySelector<SVGForeignObjectElement>(
      `[data-preview-annotation-delete-id="${annotationId}"]`,
    );

    if (!deleteButton) {
      return;
    }

    deleteButton.style.opacity = "0";
    deleteButton.style.pointerEvents = "none";
  }

  private positionDrawDeleteButton(
    deleteButton: SVGForeignObjectElement,
    annotation: PreviewToolbarDrawAnnotation,
  ): void {
    const lastPoint = annotation.points.at(-1) ?? annotation.points[0];
    if (!lastPoint) {
      return;
    }

    const buttonSize = 18;
    const offset = Math.max(annotation.strokeWidth * 0.75, 10);
    const x = Math.max(0, lastPoint.x - offset);
    const y = Math.max(0, lastPoint.y - offset);

    deleteButton.setAttribute("x", `${x}`);
    deleteButton.setAttribute("y", `${y}`);
    deleteButton.setAttribute("width", `${buttonSize}`);
    deleteButton.setAttribute("height", `${buttonSize}`);
  }

  private createDrawDeleteButton(annotationId: string): SVGForeignObjectElement {
    const foreignObject = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "foreignObject",
    );
    foreignObject.dataset["previewAnnotationDeleteId"] = annotationId;
    foreignObject.style.overflow = "visible";
    foreignObject.style.opacity = "0";
    foreignObject.style.pointerEvents = "none";
    foreignObject.style.zIndex = "3";

    const buttonHost = document.createElementNS("http://www.w3.org/1999/xhtml", "div");
    buttonHost.style.width = "18px";
    buttonHost.style.height = "18px";
    buttonHost.style.display = "flex";
    buttonHost.style.alignItems = "center";
    buttonHost.style.justifyContent = "center";

    const deleteButton = document.createElement("button");
    deleteButton.type = "button";
    deleteButton.setAttribute(
      "aria-label",
      MESSAGES.DOCX_PREVIEW.DELETE_HIGHLIGHT_LABEL,
    );
    deleteButton.title = MESSAGES.DOCX_PREVIEW.DELETE_HIGHLIGHT_LABEL;
    deleteButton.style.display = "inline-flex";
    deleteButton.style.alignItems = "center";
    deleteButton.style.justifyContent = "center";
    deleteButton.style.width = "18px";
    deleteButton.style.height = "18px";
    deleteButton.style.border = "0";
    deleteButton.style.borderRadius = "999px";
    deleteButton.style.padding = "0";
    deleteButton.style.background = "#f2f2f2";
    deleteButton.style.color = "#6b7280";
    deleteButton.style.cursor = "pointer";
    deleteButton.style.boxShadow = "0 0 0 1px rgba(107, 114, 128, 0.35)";
    deleteButton.innerHTML = `
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" width="10" height="10">
        <path d="M3.2 3.2 12.8 12.8M12.8 3.2 3.2 12.8" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.8" />
      </svg>
    `;
    deleteButton.addEventListener("mouseenter", () => {
      foreignObject.style.opacity = "1";
      foreignObject.style.pointerEvents = "auto";
    });
    deleteButton.addEventListener("mouseleave", () => {
      foreignObject.style.opacity = "0";
      foreignObject.style.pointerEvents = "none";
    });
    deleteButton.addEventListener("mousedown", (event) => {
      event.preventDefault();
      event.stopPropagation();
    });
    deleteButton.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.removeAnnotation(annotationId);
    });

    buttonHost.appendChild(deleteButton);
    foreignObject.appendChild(buttonHost);
    return foreignObject;
  }

  private buildDrawPath(points: PreviewToolbarDrawPoint[]): string {
    if (points.length === 0) {
      return "";
    }

    return points.reduce((pathData, point, index) => {
      const command = index === 0 ? "M" : "L";
      return `${pathData}${command}${point.x} ${point.y} `;
    }, "").trim();
  }

  private getDrawStrokeWidth(wrapper: HTMLElement): number {
    const previewDimensions = this.getNaturalDocumentDimensions(wrapper);
    return Number((Math.max(previewDimensions.width, previewDimensions.height) * 0.012).toFixed(2));
  }

  private getLocalDrawPoint(
    wrapper: HTMLElement,
    clientX: number,
    clientY: number,
  ): PreviewToolbarDrawPoint | null {
    const previewDimensions = this.getNaturalDocumentDimensions(wrapper);
    if (previewDimensions.width <= 0 || previewDimensions.height <= 0) {
      return null;
    }

    const zoomLevel = this.getEffectiveZoomLevel(this.getFitScale(wrapper));
    if (zoomLevel <= 0) {
      return null;
    }

    const wrapperRect = wrapper.getBoundingClientRect();
    const relativeX = clientX - wrapperRect.left;
    const relativeY = clientY - wrapperRect.top;

    if (
      relativeX < 0 ||
      relativeY < 0 ||
      relativeX > wrapperRect.width ||
      relativeY > wrapperRect.height
    ) {
      return null;
    }

    const scaledX = relativeX / zoomLevel;
    const scaledY = relativeY / zoomLevel;

    switch (this.appPreviewToolbar.rotationDegrees) {
      case 90:
        return {
          x: this.clampDrawCoordinate(scaledY, previewDimensions.width),
          y: this.clampDrawCoordinate(
            previewDimensions.height - scaledX,
            previewDimensions.height,
          ),
        };
      case 180:
        return {
          x: this.clampDrawCoordinate(
            previewDimensions.width - scaledX,
            previewDimensions.width,
          ),
          y: this.clampDrawCoordinate(
            previewDimensions.height - scaledY,
            previewDimensions.height,
          ),
        };
      case 270:
        return {
          x: this.clampDrawCoordinate(
            previewDimensions.width - scaledY,
            previewDimensions.width,
          ),
          y: this.clampDrawCoordinate(scaledX, previewDimensions.height),
        };
      case 0:
      default:
        return {
          x: this.clampDrawCoordinate(scaledX, previewDimensions.width),
          y: this.clampDrawCoordinate(scaledY, previewDimensions.height),
        };
    }
  }

  private clampDrawCoordinate(value: number, maxValue: number): number {
    return Number(Math.min(maxValue, Math.max(0, value)).toFixed(2));
  }

  private cancelActiveDrawHighlight(): void {
    this.isDrawingHighlight = false;
    this.activeDrawAnnotation = null;
    const wrapper = this.ensurePreviewWrapper();
    wrapper
      ?.querySelectorAll<SVGElement>(".docx-preview-draw-layer")
      .forEach((element) => {
        if (!element.querySelector("[data-preview-annotation-id]")) {
          element.remove();
        }
      });
  }
}
